/**
 * Transporte del asistente: fetch crudo + SSE.
 *
 * `supabase.functions.invoke` no puede streamear, así que aquí va un fetch con
 * el token DEL USUARIO (no la publishable key: el backend corre bajo RLS con
 * esa identidad) y un parser SSE por líneas — el mismo patrón que el chatbot
 * de la landing (AIChatbot.tsx), pero despachando tramas tipadas `{type: …}`
 * en vez de deltas de OpenAI.
 *
 * Contrato de tramas (espejo de supabase/functions/ipm-ai-assistant/sse.ts):
 * meta · tool_start · tool_end · token · replace · sources · nav · cards · doc ·
 * done · error, y `[DONE]`.
 *
 * Protocolo v2 (este cliente lo pide con `stream_version: 2`): el texto llega EN
 * DIRECTO en `token` (ya saneado línea a línea en el servidor) y `replace`
 * SUSTITUYE todo el texto en pantalla: a mitad de turno lo reinicia (el modelo
 * escribió algo antes de llamar a una herramienta) y al final deja el texto
 * definitivo, que es exactamente el que se guarda. Un cliente sin v2 (la app
 * nativa con el bundle ya publicado) recibe el protocolo de siempre.
 *
 * `request_id`: un uuid por mensaje, el MISMO en el reintento automático, para
 * que el servidor no guarde el turno dos veces.
 *
 * Si un 200 llega como application/json (backend aún sin stream, o fallback),
 * se resuelve como una respuesta de un solo golpe — despliegue en cualquier
 * orden sin romper nada.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ToolActivity {
  id: string;
  name: string;
  label?: string;
  status: "running" | "done" | "error";
  total?: number;
}

export interface AssistantSource {
  title: string;
  url: string;
  consulted_at?: string;
  kind?: string;
}

/**
 * Filas que el backend manda para pintarlas como tarjetas. Son las que devolvió
 * la consulta, NO lo que el modelo redacte después: una tarjeta se lee como un
 * dato duro, así que nunca la escribe el modelo (ver CardGroup en agentLoop).
 */
export interface AssistantCardGroup {
  /**
   * «task»: filas de get_tasks. «action»: botones de get_app_help/open_in_app,
   * cada item {id, label, url? | panel?: 'taskVoice'|'scoutingVoice', path?,
   * priority}; la URL la construye el servidor, el modelo nunca la escribe.
   * «cuaderno» (CQ4b): la tarjeta de descarga del cuaderno, item {year,
   * titularLocationId, draftId}. «cuaderno_change»: UNA propuesta de cambios
   * del cuaderno (src/lib/cuaderno/cambios.ts), que la tarjeta vuelve a validar.
   * «email» (27-09-2026, A21): UNA propuesta de correo de propose_email, item
   * {send_id, to[], subject, message, attachment}; envía la persona («Enviar»).
   * «marca_cambio» (F4 de «Personalizar informes», 29-09-2026): UN dato de la
   * cabecera de los informes, item {v, campo, valor, valor_label}; lo guarda el
   * admin con «Guardar en la cabecera» y el chat rehace el informe.
   * «estructura_cambio» (23-10-2026, «Campos y numeración»): campos nuevos,
   * valores y numeraciones de la estructura, item {v, ops, no_casan}; lo guarda
   * el admin con «Guardar» (src/lib/estructura/aplicarCambios.ts).
   */
  kind: AssistantCardKind;
  items: Array<Record<string, unknown>>;
}

/** Los kinds de tarjeta que sabe pintar el chat (espejo de CARD_KINDS en agentLoop.ts). */
export const ASSISTANT_CARD_KINDS = ["task", "action", "cuaderno", "cuaderno_change", "email", "document_fill", "marca_cambio", "estructura_cambio"] as const;
export type AssistantCardKind = (typeof ASSISTANT_CARD_KINDS)[number];

/** Los seguimientos automáticos, cada uno en su modo (espejo de mode.ts#autoDelTurno). */
export type AssistantAuto = "cuaderno_aplicado" | "marca_guardada";

/** ¿Viaja este seguimiento con este modo? «cuaderno_aplicado» solo en el cuaderno; «marca_guardada» solo en el normal. PURA. */
export function autoValido(mode: string | null | undefined, auto: AssistantAuto | null | undefined): boolean {
  if (auto === "cuaderno_aplicado") return mode === "cuaderno";
  if (auto === "marca_guardada") return !mode;
  return false;
}

/** Una tabla de un informe compuesto. */
export interface AssistantDocSection {
  title: string;
  filters?: Array<{ label: string; value: string }>;
  kpis?: Array<{ label: string; value: string }>;
  columns: Array<{ header: string; width?: number; align?: "left" | "right"; muted?: boolean; hideIfEmpty?: boolean }>;
  rows: string[][];
}

/** Las filas de todo el informe (todas sus secciones). */
export function filasDelInforme(doc: Pick<AssistantDocSpec, "rows" | "sections">): number {
  return doc.sections?.length ? doc.sections.reduce((n, s) => n + (s.rows?.length ?? 0), 0) : doc.rows.length;
}

/**
 * Especificación de un informe; lo pinta el navegador en PDF (constructor de
 * /reports) y en Excel (lib/reportXlsx) con las mismas filas.
 */
export interface AssistantDocSpec {
  report_id: string;
  title: string;
  orientation?: "portrait" | "landscape";
  date_start?: string;
  date_end?: string;
  filters: Array<{ label: string; value: string }>;
  kpis: Array<{ label: string; value: string }>;
  columns: Array<{ header: string; width?: number; align?: "left" | "right"; muted?: boolean; hideIfEmpty?: boolean }>;
  rows: string[][];
  /** Formatos que se ofrecen. Sin el campo (backend anterior), los dos. */
  formats?: Array<"pdf" | "xlsx">;
  /** Locale en que vienen escritas las cifras (el Excel las vuelve números con él). */
  locale?: string;
  /**
   * Solo al reabrir una conversación: el informe se guardó sin filas (los de
   * antes del 24-09-2026, o uno que no cupo) y ya no se puede descargar.
   */
  unavailable?: boolean;
  /** Cuántas filas tenía, cuando se guardó sin ellas. */
  row_count?: number;
  /** Informe compuesto: varias tablas una debajo de otra (arriba van las de la primera). */
  sections?: AssistantDocSection[];
  /** Con qué se armó (build_report): el siguiente turno lo rehace con un cambio. */
  build_args?: Record<string, unknown>;
  /**
   * F4 de «Personalizar informes»: los datos de la empresa que este informe lleva en
   * su cabecera, SOLO nombres (M4); los valores los pone el navegador desde la marca
   * (lib/marca/cabeceraInforme). `faltan`: los pedidos que aún no están guardados.
   */
  cabecera?: { campos: string[]; quitar?: string[]; faltan?: string[] };
}

/**
 * El texto de un mensaje del asistente tal como vuelve en el historial: con los
 * argumentos de los informes que armó (28-09-2026). El historial solo lleva
 * texto, y sin esto «quítale también el Movento» obligaba al modelo a adivinar
 * de qué informe se hablaba (finca, fechas, lo ya quitado). PURA.
 */
/** Tope de la nota (igual que REPORT_NOTE_MAX_CHARS del servidor, menos el envoltorio). */
const NOTA_INFORME_MAX = 5_900;

/**
 * Los argumentos del informe en una línea que quepa: si una tabla libre trae
 * demasiadas filas, se quitan sus filas (se dice cuántas) antes que cortar el
 * JSON por la mitad. PURA.
 */
export function argsParaHistorial(args: Record<string, unknown>): string {
  let s = JSON.stringify(args);
  if (s.length <= NOTA_INFORME_MAX) return s;
  const sinFilas = (x: Record<string, unknown>) =>
    Array.isArray(x.custom_rows) ? { ...x, custom_rows: `(${x.custom_rows.length} rows omitted: rebuild them from the data)` } : x;
  const ligero = {
    ...sinFilas(args),
    ...(Array.isArray(args.sections) ? { sections: (args.sections as Record<string, unknown>[]).map((sec) => (sec && typeof sec === "object" ? sinFilas(sec) : sec)) } : {}),
  };
  s = JSON.stringify(ligero);
  return s.length <= NOTA_INFORME_MAX ? s : JSON.stringify({ report: args.report, note: "arguments too long to keep: ask what to keep" });
}

export function contenidoParaHistorial(content: string, docs?: AssistantDocSpec[] | null): string {
  const conArgs = (docs ?? []).filter((d) => d.build_args && typeof d.build_args === "object");
  if (!conArgs.length) return content;
  // Delante y enteras: el servidor guarda estas notas aparte del recorte del texto (6000 caracteres).
  const lineas = conArgs.map((d) => `[report built: build_report ${argsParaHistorial(d.build_args!)}]`);
  return `${lineas.join("\n")}\n${content}`;
}

/**
 * Los informes guardados con un mensaje (`context.docs`), listos para pintarlos
 * al reabrir la conversación. PURA: la prueba assistantReportPersist.test.ts.
 * Los que llegaron enteros vuelven tal cual; los guardados solo como ficha
 * vuelven como `unavailable`, para que se vea que hubo un informe y cuál.
 */
export function restoredDocs(raw: unknown): AssistantDocSpec[] {
  if (!Array.isArray(raw)) return [];
  const out: AssistantDocSpec[] = [];
  for (const d of raw) {
    if (!d || typeof d !== "object") continue;
    const doc = d as Record<string, unknown>;
    if (typeof doc.title !== "string" || typeof doc.report_id !== "string") continue;
    if (Array.isArray(doc.rows) && Array.isArray(doc.columns) && doc.rows_dropped !== true) {
      out.push({
        ...(doc as unknown as AssistantDocSpec),
        filters: Array.isArray(doc.filters) ? (doc.filters as AssistantDocSpec["filters"]) : [],
        kpis: Array.isArray(doc.kpis) ? (doc.kpis as AssistantDocSpec["kpis"]) : [],
      });
    } else {
      out.push({
        report_id: doc.report_id,
        title: doc.title,
        date_start: typeof doc.date_start === "string" ? doc.date_start : undefined,
        date_end: typeof doc.date_end === "string" ? doc.date_end : undefined,
        filters: [],
        kpis: [],
        columns: [],
        rows: [],
        unavailable: true,
        ...(typeof doc.row_count === "number" ? { row_count: doc.row_count } : {}),
      });
    }
  }
  return out;
}

export interface AssistantStreamMeta {
  docs?: AssistantDocSpec[];
  cards?: AssistantCardGroup[];
  sources?: AssistantSource[];
  nav?: AssistantNavTarget[];
  tools_used?: string[];
  latency_ms?: number;
  rounds?: number;
  finish?: string;
  /** El modo que ATENDIÓ la edge (trama `meta`): «cuaderno» o nada (normal, o edge anterior). */
  mode?: string;
}

/**
 * Un sitio de la app al que el asistente puede llevarte. Se pinta como botón y
 * navega por dentro (react-router): a diferencia de `sources`, que son enlaces
 * externos, esto NO puede ser un <a href> — recargaría la SPA entera.
 *
 * La ruta la fija el servidor a partir de la guía de uso; el modelo no la
 * escribe, así que no puede inventársela.
 */
export interface AssistantNavTarget {
  route: string;
  label: string;
}

export interface StreamHandlers {
  onDelta: (text: string) => void;
  /** Sustituye TODO el texto del mensaje (protocolo v2). */
  onReplace?: (text: string) => void;
  onTool: (t: ToolActivity) => void;
  onMeta: (m: AssistantStreamMeta) => void;
}

export interface StreamPayload {
  message: string;
  language: string;
  conversationId?: string | null;
  history: Array<{ role: string; content: string }>;
  /** «@ Cuaderno de campo» (CQ4b): viaja como `body.mode`; sin él, el modo normal. */
  mode?: "cuaderno" | null;
  /** El cuaderno del modo: año y titular (D14). */
  cuaderno?: { year?: number; titular_location_id?: string | null } | null;
  /**
   * CU7: turno AUTOMÁTICO tras «Aplicar» en una tarjeta del cuaderno. El servidor
   * pone el texto y guarda la fila de la persona marcada (`context.auto`). F4
   * (29-09-2026): «marca_guardada», tras «Guardar en la cabecera», en el modo normal.
   */
  auto?: AssistantAuto | null;
  /** Documentos subidos a la conversación (ids de `assistant_documents`, los últimos 5). */
  documents?: string[];
  /** Los que la persona adjuntó con ESTE mensaje (se guardan con su fila). */
  attached?: string[];
}

export class AssistantHttpError extends Error {
  status: number;
  constructor(status: number, message?: string) {
    super(message || `HTTP ${status}`);
    this.status = status;
  }
}

/** Errores → clave i18n (el componente los traduce). */
export function assistantErrorKey(err: unknown): string {
  if (err instanceof AssistantHttpError) {
    if (err.status === 401) return "ipmAssistant.errorAuth";
    if (err.status === 403) return "ipmAssistant.errorForbidden";
    if (err.status === 429) return "ipmAssistant.errorRateLimit";
    if (err.status === 402) return "ipmAssistant.errorPayment";
  }
  return "ipmAssistant.errorComm";
}

const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ipm-ai-assistant`;

/**
 * Despacha UNA trama SSE ya parseada a los manejadores. PURA (sin red ni
 * React): la prueba assistantStreamFrames.test.ts. Devuelve si trajo texto
 * (para decidir si un corte se puede reintentar sin duplicar). Una trama
 * `error` lanza AssistantHttpError con el estado equivalente.
 */
export function dispatchAssistantFrame(frame: Record<string, unknown>, handlers: StreamHandlers): { text: boolean } {
  switch (frame.type) {
    case "token":
      if (typeof frame.d === "string") {
        handlers.onDelta(frame.d);
        return { text: frame.d.length > 0 };
      }
      return { text: false };
    case "replace":
      if (typeof frame.text === "string") {
        handlers.onReplace?.(frame.text);
        return { text: frame.text.length > 0 };
      }
      return { text: false };
    case "tool_start":
      handlers.onTool({
        id: String(frame.id ?? frame.name),
        name: String(frame.name ?? ""),
        label: typeof frame.label === "string" ? frame.label : undefined,
        status: "running",
      });
      return { text: false };
    case "tool_end":
      handlers.onTool({
        id: String(frame.id ?? frame.name),
        name: String(frame.name ?? ""),
        status: frame.ok === false ? "error" : "done",
        ...(typeof frame.total === "number" ? { total: frame.total } : {}),
      });
      return { text: false };
    case "sources":
      if (Array.isArray(frame.sources)) {
        handlers.onMeta({ sources: frame.sources as AssistantSource[] });
      }
      return { text: false };
    case "nav":
      if (Array.isArray(frame.nav) && frame.nav.length > 0) {
        handlers.onMeta({ nav: frame.nav as AssistantNavTarget[] });
      }
      return { text: false };
    case "doc":
      if (frame.doc) handlers.onMeta({ docs: [frame.doc as AssistantDocSpec] });
      return { text: false };
    case "cards":
      // Llega un grupo por trama; se acumulan en el mensaje. Un kind que el chat
      // no sabe pintar se ignora (no se pinta como otra cosa).
      if (
        Array.isArray(frame.items) && frame.items.length > 0 &&
        (ASSISTANT_CARD_KINDS as readonly string[]).includes(frame.kind as string)
      ) {
        handlers.onMeta({
          cards: [{
            kind: frame.kind as AssistantCardKind,
            items: frame.items as Array<Record<string, unknown>>,
          }],
        });
      }
      return { text: false };
    case "meta":
      // Solo interesa el modo que atendió la edge (el resto es telemetría).
      if (typeof frame.mode === "string") handlers.onMeta({ mode: frame.mode });
      return { text: false };
    case "done":
      handlers.onMeta({
        tools_used: frame.tools_used as string[] | undefined,
        latency_ms: frame.latency_ms as number | undefined,
        rounds: frame.rounds as number | undefined,
        finish: frame.finish as string | undefined,
      });
      return { text: false };
    case "error": {
      const code = String(frame.code ?? "");
      const status = code === "rate_limited" ? 429 : code === "credits" ? 402 : code === "unauthorized" ? 401 : 500;
      throw new AssistantHttpError(status, String(frame.message ?? "stream error"));
    }
    default:
      return { text: false };
  }
}

/** Un uuid por mensaje (el mismo en su reintento). Sin crypto.randomUUID (navegador viejo), sin id. */
function newRequestId(): string | undefined {
  try {
    return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function" ? crypto.randomUUID() : undefined;
  } catch {
    return undefined;
  }
}

export function useAssistantStream() {
  const abortRef = useRef<AbortController | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);

  useEffect(() => () => abortRef.current?.abort(), []);

  const abort = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  /**
   * Lanza la petición. Resuelve cuando el stream termina (o el usuario aborta).
   * `receivedText` distingue «se cortó antes de empezar» (reintentable) de
   * «se cortó a mitad» (NO reintentar: duplicaría texto). `transport` dice si
   * contestó el backend nuevo (sse, persiste él) o el antiguo (json, persiste
   * el cliente como siempre).
   */
  const send = useCallback(async (
    payload: StreamPayload,
    handlers: StreamHandlers,
  ): Promise<{ aborted: boolean; receivedText: boolean; transport: "sse" | "json" }> => {
    const controller = new AbortController();
    abortRef.current = controller;
    setIsStreaming(true);
    let receivedText = false;
    // Fuera de doFetch: el reintento de abajo lleva el MISMO id.
    const requestId = newRequestId();

    const doFetch = async (): Promise<{ aborted: boolean; receivedText: boolean; transport: "sse" | "json" }> => {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new AssistantHttpError(401);

      const res = await fetch(FN_URL, {
        method: "POST",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          message: payload.message,
          language: payload.language,
          /* La zona del aparato. El servidor recorta con ella los rangos de día
             («hoy», «mañana», «esta semana»): sin esto lo hacía en UTC y sus
             días no cuadraban con los que pinta el calendario. */
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          /* En qué pantalla está preguntando. Sirve para las preguntas que
             señalan («¿cómo añado uno de estos?», «¿qué es esto?»), que sin la
             página de referencia no tienen respuesta posible. Solo la ruta:
             ningún id ni parámetro. */
          current_route: typeof window !== "undefined" ? window.location.pathname : undefined,
          conversationHistory: payload.history,
          stream: true,
          // Texto en directo con `replace` (ver cabecera).
          stream_version: 2,
          ...(requestId ? { request_id: requestId } : {}),
          /* «@ Cuaderno de campo»: el modo y su cuaderno. Sin modo no viaja nada
             nuevo, así que la edge ve exactamente la petición de siempre. */
          ...(payload.mode ? { mode: payload.mode } : {}),
          ...(payload.mode && payload.cuaderno ? { cuaderno: payload.cuaderno } : {}),
          ...(autoValido(payload.mode, payload.auto) ? { auto: payload.auto } : {}),
          // Documentos subidos (28-09-2026): sin ellos no viaja nada nuevo.
          ...(payload.documents?.length ? { documents: payload.documents, attached: payload.attached ?? [] } : {}),
          persist: !!payload.conversationId,
          ...(payload.conversationId ? { conversation_id: payload.conversationId } : {}),
        }),
      });

      if (!res.ok) {
        let msg: string | undefined;
        try { msg = (await res.json())?.error; } catch { /* cuerpo no-JSON */ }
        throw new AssistantHttpError(res.status, msg);
      }

      // Fallback: backend respondió JSON de un golpe (contrato legado).
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = await res.json();
        if (data?.error) throw new AssistantHttpError(500, data.error);
        if (typeof data?.response === "string") {
          receivedText = true;
          handlers.onDelta(data.response);
          handlers.onMeta({
            cards: data.structured?.cards,
            docs: data.structured?.docs,
            sources: data.meta?.sources,
            tools_used: data.meta?.packs_used,
            latency_ms: data.meta?.latency_ms,
            rounds: data.meta?.rounds,
          });
        }
        return { aborted: false, receivedText, transport: "json" };
      }

      if (!res.body) throw new AssistantHttpError(500, "empty body");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let nl: number;
        while ((nl = buffer.indexOf("\n")) !== -1) {
          const line = buffer.slice(0, nl).trim();
          buffer = buffer.slice(nl + 1);
          if (!line || line.startsWith(":")) continue; // ping/comentario
          if (!line.startsWith("data: ")) continue;
          const raw = line.slice(6);
          if (raw === "[DONE]") continue;

          let frame: Record<string, unknown>;
          try {
            frame = JSON.parse(raw);
          } catch {
            /* Una línea COMPLETA (ya vimos su \n) que no parsea está corrupta,
               no partida — las tramas partidas por el chunking aún no tienen \n
               y esperan solas en el buffer. Re-bufferizarla la dejaría en bucle
               para siempre; se descarta. */
            continue;
          }

          /* «Hay texto en pantalla», no «llegó texto alguna vez»: un preámbulo que el
             servidor retira con `replace:""` (antes de una herramienta) deja la pantalla
             vacía, y un corte de red después tiene que poder reintentarse (antes el
             booleano se quedaba pegado y el globo desaparecía con un error). */
          const shown = dispatchAssistantFrame(frame, handlers).text;
          if (frame.type === "replace") receivedText = shown;
          else if (shown) receivedText = true;
        }
      }
      return { aborted: false, receivedText, transport: "sse" };
    };

    try {
      try {
        return await doFetch();
      } catch (err) {
        if (controller.signal.aborted) return { aborted: true, receivedText, transport: "sse" };
        // Reintento único SOLO si aún no llegó nada (repetir a mitad duplicaría texto).
        const retriable = !(err instanceof AssistantHttpError) && !receivedText;
        if (!retriable) throw err;
        await new Promise((r) => setTimeout(r, 1000));
        if (controller.signal.aborted) return { aborted: true, receivedText, transport: "sse" };
        // Pantalla limpia antes de repetir (el reintento vuelve a mandar el texto entero).
        handlers.onReplace?.("");
        return await doFetch();
      }
    } catch (err) {
      if (controller.signal.aborted) return { aborted: true, receivedText, transport: "sse" };
      throw err;
    } finally {
      setIsStreaming(false);
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, []);

  return { send, abort, isStreaming };
}
